/* eslint-disable no-new */
/* eslint-disable no-undef */
const INFO_WINDOW_WIDTH = 320
const WINDOW_OFFSET = 180
class CampsitesSearch extends HTMLElement {
  constructor () {
    super()
    this.map = null
    this.markers = new Map()
    this.campsiteMarkers = []
    this.bounds = null
    this.EARTH_RADIUS_MILES = 3959
    this.MAP_BOUNDS_PADDING = { top: 20, right: 20, bottom: 20, left: 20 }
    this.INFO_WINDOW_WIDTH = 300

    this.filterButton = null
    this.filterDropdown = null
    this.filterCheckboxes = null
    this.campsiteCards = null
    this.activeFiltersList = new Set()
    this.translations = {}

    this.currentPage = 1
    this.itemsPerPage = 12
    this.paginationContainer = null

    this.currentCampsites = []
    this.isLoading = false
    this.clearButton = null
  }

  connectedCallback () {
    this.initializeElements()

    this.filterButton = this.querySelector('.campsites__filters--button')
    this.filterDropdown = this.querySelector('.campsites__filters-dropdown')
    this.filterCheckboxes = this.querySelectorAll('.campsites__filters-dropdown input')
    this.campsiteCards = this.querySelectorAll('.campsites__locations-list--item')

    if (this.filterButton) {
      const buttonText = this.filterButton.querySelector('.campsites__filters--button--text')
      this.translations = {
        filters: buttonText ? buttonText.textContent : 'Filters',
      }
    }

    if (this.filterButton && this.filterDropdown) {
      this.setupFilters()
    }

    this.itemsPerPage = parseInt(this.dataset.paginationPerPage) || 12
    this.paginationContainer = this.querySelector('.campsites__locations-pagination')

    this.addEventListeners()
    this.initializeWithSortedCampsites()
    this.initializeMobileNav()
  }

  initializeElements () {
    this.searchForm = document.querySelector('.campsites__search-form') || null
    if (this.searchForm) {
      this.searchInput = this.searchForm.querySelector('.campsites__search-input input')
      this.clearButton = this.searchForm.querySelector('.campsites__search-input-clear')
    }
    this.locationsContainer = this.querySelector('.campsites__locations')
    this.mapContainer = this.querySelector('.campsites__locations-map')
    this.cardViewButton = this.querySelector('.campsites__nav--card-view')
    this.mapViewButton = this.querySelector('.campsites__nav--map-view')
    this.cardViewColumn = this.querySelector('.campsites__locations-column--list')
    this.mapViewColumn = this.querySelector('.campsites__locations-column--map')
  }

  initializeMobileNav () {
    this.mapViewColumn.classList.add('hidden')
    this.cardViewButton.classList.add('tb-timeline-link-active')
    this.mapViewButton.classList.remove('tb-timeline-link-active')
  }

  addEventListeners () {
    if (this.searchForm) {
      this.searchForm.addEventListener('submit', (event) => {
        event.preventDefault()
        this.handleSearchSubmit()
      })
    }

    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => {
        this.toggleClearButton()
      })
    }

    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => {
        this.resetSearch()
      })
    }

    if (this.cardViewButton) {
      this.cardViewButton.addEventListener('click', () => {
        this.switchToView('card')
      })
    }

    if (this.mapViewButton) {
      this.mapViewButton.addEventListener('click', () => {
        this.switchToView('map')
      })
    }

    window.addEventListener('resize', () => {
      if (this.map) {
        google.maps.event.trigger(this.map, 'resize')
        this.fitMapBounds()
      }
    })
  }

  toggleClearButton () {
    if (!this.clearButton) return

    if (this.searchInput.value) {
      this.clearButton.classList.remove('hidden')
      this.clearButton.classList.add('flex')
    } else {
      this.clearButton.classList.add('hidden')
      this.clearButton.classList.remove('flex')
    }
  }

  resetSearch () {
    if (this.isLoading) return

    this.searchInput.value = ''
    this.toggleClearButton()

    // Hide search title
    const searchTitle = this.querySelector('.campsites__locations-search-title')
    if (searchTitle) {
      searchTitle.classList.add('hidden')
    }

    // Don't reset entire UI, maintain current state but remove search-specific filtering
    if (this.locationsContainer && !this.locationsContainer.classList.contains('hidden')) {
      // If we've already done a search and showing results, keep showing them
      // Just reset to the initial view with all campsites (keeping filters)

      if (this.activeFiltersList.size > 0) {
        // If filters are applied, just reapply them
        this.filterCampsites()
      } else {
        // If no filters, show all campsites
        this.currentPage = 1
        this.showInitialResults(this.currentCampsites)
      }
    }
  }

  async handleSearchSubmit () {
    if (this.loading) return

    this.loading = true
    this.isLoading = true

    // Close any open info window
    if (this.customInfoWindow) {
      this.customInfoWindow.setMap(null)
    }

    const searchQuery = this.searchInput.value.trim()
    if (!searchQuery) {
      this.loading = false
      this.isLoading = false
      return
    }

    // Record the start time for minimum loading time
    const startTime = Date.now()

    try {
      const searchCoords = await this.getSearchCoordinates(searchQuery)
      if (!searchCoords) throw new Error('Invalid location')

      const nearestCampsites = this.findNearestCampsites(searchCoords)

      this.initializeMap(searchCoords, nearestCampsites)
      this.showResults(nearestCampsites)

      // Show search title with the search query
      const searchTitle = this.querySelector('.campsites__locations-search-title')
      if (searchTitle) {
        searchTitle.innerHTML = this.getAttribute('data-search-results-text').replace('{{ input_value }}', `<strong>${searchQuery}</strong>`)
        searchTitle.classList.remove('hidden')
      }

      if (this.activeFiltersList.size > 0) {
        this.filterCampsites()
      }

      // Ensure minimum loading time of 800ms
      await this.ensureMinimumLoadingTime(startTime, 800)

      this.locationsContainer.classList.remove('hidden')

      const buttonToAlign = this.cardViewButton
      const buttonRect = buttonToAlign.getBoundingClientRect()
      const scrollTop = window.pageYOffset + buttonRect.top - 62
      window.scrollTo({
        top: scrollTop,
        behavior: 'smooth',
      })

      // Show clear button after successful search
      this.toggleClearButton()

      this.loading = false
      this.isLoading = false
    } catch (error) {
      console.error('Error:', error)
      this.loading = false
      this.isLoading = false
    }
  }

  async ensureMinimumLoadingTime (startTime, minTime) {
    const elapsedTime = Date.now() - startTime
    const remainingTime = Math.max(0, minTime - elapsedTime)

    if (remainingTime > 0) {
      await new Promise(resolve => setTimeout(resolve, remainingTime))
    }
  }

  async getSearchCoordinates (address) {
    try {
      const geocoder = new google.maps.Geocoder()
      const result = await new Promise((resolve, reject) => {
        geocoder.geocode({ address, region: 'uk' }, (results, status) => {
          if (status === 'OK') {
            resolve(results[0].geometry.location)
          } else {
            reject(new Error(`Geocoding failed: ${status}`))
          }
        })
      })

      const destinations = window.campsites.map(campsite => {
        const [lat, lng] = campsite.longitude_latitude.split(',').map(coord => parseFloat(coord.trim()))
        return new google.maps.LatLng(lat, lng)
      })

      const distanceMatrix = await new Promise((resolve, reject) => {
        const service = new google.maps.DistanceMatrixService()
        service.getDistanceMatrix(
          {
            origins: [result],
            destinations,
            travelMode: google.maps.TravelMode.DRIVING,
            unitSystem: google.maps.UnitSystem.METRIC,
          },
          (response, status) => {
            if (status === 'OK') {
              resolve(response)
            } else {
              reject(new Error(`Distance Matrix failed: ${status}`))
            }
          },
        )
      })

      const campsitesWithDistances = window.campsites.map((campsite, index) => {
        const element = distanceMatrix.rows[0].elements[index]
        const [lat, lng] = campsite.longitude_latitude.split(',').map(coord => parseFloat(coord.trim()))
        return {
          ...campsite,
          distance: element.status === 'OK' ? Math.round(element.distance.value / 1000) : null,
          pinLat: lat,
          pinLong: lng,
        }
      })

      const sortedCampsites = campsitesWithDistances.sort((a, b) => {
        if (a.distance === null) return 1
        if (b.distance === null) return -1
        return a.distance - b.distance
      })

      this.currentCampsites = sortedCampsites

      return result
    } catch (error) {
      console.error('Error getting coordinates:', error)
      return null
    }
  }

  findNearestCampsites (searchCoords) {
    return this.currentCampsites
  }

  initializeMap (center, campsites) {
    if (!center || !center.lat || !center.lng) {
      console.error('Invalid center coordinates')
      return
    }

    const iconUrl = this.dataset.pinIconUrl.startsWith('//')
      ? `https:${this.dataset.pinIconUrl}`
      : this.dataset.pinIconUrl

    if (!this.map) {
      this.map = new google.maps.Map(this.mapContainer, {
        zoom: 7,
        center,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
      })
    } else {
      this.map.setCenter(center)
      this.map.setZoom(8)
    }

    this.map.addListener('click', () => {
      if (this.customInfoWindow) {
        this.customInfoWindow.setMap(null)
      }
    })

    this.markers.forEach((marker) => marker.setMap(null))
    this.markers.clear()
    this.campsiteMarkers = []
    if (this.customInfoWindow) {
      this.customInfoWindow.setMap(null)
    }

    this.bounds = new google.maps.LatLngBounds()

    campsites.forEach((campsite, index) => {
      if (!campsite.pinLat || !campsite.pinLong) return

      const position = {
        lat: campsite.pinLat,
        lng: campsite.pinLong,
      }

      const marker = new google.maps.Marker({
        position,
        map: this.map,
        icon: {
          url: iconUrl,
          scaledSize: new google.maps.Size(40, 49),
          anchor: new google.maps.Point(20, 49),
        },
        zIndex: 2,
      })

      marker.addListener('click', () => {
        if (this.customInfoWindow) {
          this.customInfoWindow.setMap(null)
        }
        this.customInfoWindow = this.createCustomInfoWindow(campsite, position)
        this.customInfoWindow.setMap(this.map)

        google.maps.event.trigger(this.map, 'resize')

        const point = this.map.getProjection().fromLatLngToPoint(position)
        const offsetPoint = new google.maps.Point(
          point.x,
          point.y - WINDOW_OFFSET / Math.pow(2, this.map.getZoom()),
        )
        const offsetLatLng = this.map.getProjection().fromPointToLatLng(offsetPoint)
        this.map.panTo(offsetLatLng)
      })

      this.markers.set(campsite.id, marker)
      this.campsiteMarkers.push(marker)

      if (index < 3) {
        this.bounds.extend(position)
      }
    })
  }

  handleUrlString (str) {
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[']/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  }

  createCustomInfoWindow (campsites, position) {
    const CustomInfoWindow = new google.maps.OverlayView()

    CustomInfoWindow.handleUrlString = this.handleUrlString.bind(this)

    CustomInfoWindow.onAdd = function () {
      const div = document.createElement('div')
      div.className = 'custom-info-window'
      div.style.cssText = `
        position: absolute;
        background: white;
        border: 1px solid #ccc;
        border-radius: 8px;
        padding: 0 0 20px 0;
        max-width: ${INFO_WINDOW_WIDTH}px;
        min-width: ${INFO_WINDOW_WIDTH}px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        overflow: hidden;
        z-index: 1000;
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
      `

      const heroImage = window.campsiteImages[campsites.name] || ''

      const closeButton = document.createElement('div')
      closeButton.style.cssText = `
        position: absolute;
        top: 10px;
        right: 10px;
        width: 24px;
        height: 24px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1;
      `
      closeButton.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="white" width="16" height="16">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      `
      closeButton.addEventListener('click', () => {
        if (this.div) {
          this.setMap(null)
        }
      })

      const campsiteUrl = `/pages/campsites/${this.handleUrlString(campsites.name)}`

      div.innerHTML = `
        <div class="relative h-[120px] w-full rounded-t overflow-hidden">
          <img src="${heroImage}" alt="${campsites.name}" class="w-full h-full object-cover">
          <div class="absolute inset-0 bg-gradient-to-b from-transparent from-60% to-black/80 z-[1]">
            <span class="sr-only">Gradient overlay for image</span>
          </div>
          <div class="campsites__locations-list--item-title absolute bottom-1 left-1 w-full h-full flex items-end justify-start z-[2]">
            <div class="text-white font-body font-normal normal-case text-xl p-4">${campsites.name}</div>
          </div>
        </div>
        <div class="flex flex-col gap-1 px-4 pt-4 font-body">
          <p class="text-battleship text-xs">${campsites.distance !== undefined && campsites.distance !== null ? `${campsites.distance}km` : ''}</p>
          <p class="text-sm text-night mb-2">${campsites.short_description}</p>
          <button type="button" class="button button-primary text-base w-full" style="position: relative; z-index: 2; -webkit-tap-highlight-color: transparent;">View Campsite</button>
        </div>
      `

      div.appendChild(closeButton)

      const viewCampsiteButton = div.querySelector('button')
      viewCampsiteButton.addEventListener('touchend', (e) => {
        e.preventDefault()
        e.stopPropagation()
        window.open(campsiteUrl, '_self')
      })

      viewCampsiteButton.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        window.open(campsiteUrl, '_self')
      })

      this.div = div
      const panes = this.getPanes()
      panes.floatPane.appendChild(div)
    }

    CustomInfoWindow.draw = function () {
      const overlayProjection = this.getProjection()
      const pixelPosition = overlayProjection.fromLatLngToDivPixel(position)

      if (this.div) {
        this.div.style.left = `${pixelPosition.x - INFO_WINDOW_WIDTH / 2}px`
        this.div.style.top = `${pixelPosition.y - 140}px`
        this.div.style.transform = `translateY(-${WINDOW_OFFSET}px)`
      }
    }

    CustomInfoWindow.onRemove = function () {
      if (this.div) {
        this.div.parentNode.removeChild(this.div)
        this.div = null
      }
    }

    return CustomInfoWindow
  }

  showResults (campsites) {
    this.currentCampsites = campsites

    const locationItems = this.querySelectorAll('.campsites__locations-list--item')

    this.currentPage = 1
    this.updatePagination(locationItems.length)

    locationItems.forEach((item, index) => {
      const campsite = campsites[index]
      if (!campsite) {
        item.classList.add('hidden')
        return
      }

      const shouldShow = index < this.itemsPerPage
      item.classList.toggle('hidden', !shouldShow)

      if (shouldShow) {
        const campsiteUrl = `/pages/campsites/${this.handleUrlString(campsite.name)}`

        item.classList.remove('hidden')

        item.dataset.facilities = JSON.stringify(campsite.facilities)

        const imageContainer = item.querySelector('.campsites__locations-list--item-image')
        const heroImage = window.campsiteImages[campsite.name]
        if (heroImage) {
          imageContainer.innerHTML = `
            <div class="aspect-w-16 aspect-h-9 w-full h-full">
              <img src="${heroImage}" alt="${campsite.name}" class="object-cover w-full h-full rounded-t">
            </div>
            <div class="campsites__locations-list--item-title absolute bottom-1 left-1 w-full h-full flex items-end justify-start z-[2]">
              <div class="text-white font-body font-normal normal-case text-xl p-4">${campsite.name}</div>
            </div>
          `
        }

        const locationText = item.querySelector('.campsites__locations-list--item-content-location-text')
        const distanceText = item.querySelector('.campsites__locations-list--item-content-location-distance')
        const separator = item.querySelector('.campsites__locations-list--item-content-location-separator')

        locationText.textContent = campsite.location_name

        if (campsite.distance !== null && campsite.distance !== undefined) {
          distanceText.textContent = `${campsite.distance}km`
          separator.style.display = 'inline'
        } else {
          distanceText.textContent = ''
          separator.style.display = 'none'
        }

        const facilitiesColumns = item.querySelectorAll('.campsites__locations-list--item-content-facilities-column')
        const limitedFacilities = campsite.facilities.slice(0, 6)
        const facilitiesPerColumn = Math.ceil(limitedFacilities.length / 2)

        facilitiesColumns[0].innerHTML = limitedFacilities
          .slice(0, facilitiesPerColumn)
          .map(facility => {
            const facilityHandle = facility
              .toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-+|-+$/g, '')
            return `
              <div class="campsite__facilities-item flex items-center py-2">
                <span class="campsite__facilities-item-icon inline-flex items-center justify-center h-4 w-4 mr-2 shrink-0">
                  <svg class="w-full h-full">
                    <use href="#facility-${facilityHandle}"></use>
                  </svg>
                </span>
                <span class="campsite__facilities-item-text text-xs lg:text-md">
                  ${facility}
                </span>
              </div>
            `
          })
          .join('')

        facilitiesColumns[1].innerHTML = limitedFacilities
          .slice(facilitiesPerColumn)
          .map(facility => {
            const facilityHandle = facility
              .toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-+|-+$/g, '')
            return `
              <div class="campsite__facilities-item flex items-center py-2">
                <span class="campsite__facilities-item-icon inline-flex items-center justify-center h-4 w-4 mr-2 shrink-0">
                  <svg class="w-full h-full">
                    <use href="#facility-${facilityHandle}"></use>
                  </svg>
                </span>
                <span class="campsite__facilities-item-text text-xs lg:text-md">
                  ${facility}
                </span>
              </div>
            `
          })
          .join('')

        const shortDescription = item.querySelector('.campsites__locations-list--item-content-short-description')
        shortDescription.textContent = campsite.short_description

        const button = item.querySelector('a.button')
        const cardAnchor = item.querySelector('a[aria-label]')

        button.href = campsiteUrl
        button.textContent = this.getAttribute('data-view-campsite-text')
        cardAnchor.href = campsiteUrl
      }
    })
  }

  switchToView (view) {
    this.currentView = view
    this.toggleViewColumns(view === 'card')

    const clickedButton = view === 'card' ? this.cardViewButton : this.mapViewButton
    const buttonRect = clickedButton.getBoundingClientRect()
    const scrollTop = window.pageYOffset + buttonRect.top - 62

    window.scrollTo({
      top: scrollTop,
      behavior: 'smooth',
    })

    if (view === 'map' && this.map) {
      google.maps.event.trigger(this.map, 'resize')

      const searchLocation = this.map.getCenter()
      this.map.setCenter(searchLocation)
      this.map.setZoom(8)
    }
  }

  toggleViewColumns (showCard) {
    this.cardViewColumn.classList.toggle('hidden', !showCard)
    this.mapViewColumn.classList.toggle('hidden', showCard)
    this.cardViewButton.classList.toggle('tb-timeline-link-active', showCard)
    this.mapViewButton.classList.toggle('tb-timeline-link-active', !showCard)
  }

  fitMapBounds () {
    if (this.bounds && this.map) {
      const visibleMarkers = this.campsiteMarkers.filter(marker => marker.getVisible())

      if (visibleMarkers.length === 1) {
        const marker = visibleMarkers[0]
        this.map.setCenter(marker.getPosition())
        this.map.setZoom(10)
      } else {
        const searchMarker = this.map.getCenter()
        this.map.setCenter(searchMarker)
        this.map.setZoom(10)
      }
    }
  }

  calculateDistance (point1, point2) {
    const lat1 = this.toRadians(point1.lat)
    const lat2 = this.toRadians(point2.lat)
    const dLat = this.toRadians(point2.lat - point1.lat)
    const dLon = this.toRadians(point2.lng - point1.lng)

    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1) * Math.cos(lat2) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2)

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return Math.round(this.EARTH_RADIUS_MILES * c)
  }

  toRadians (degrees) {
    return degrees * (Math.PI / 180)
  }

  getMarkerIcon (color, iconUrl) {
    const pinPath = 'M 12,0 C 5.37,0 0,5.37 0,12 c 0,7.97 12,20 12,20 c 0,0 12,-12.03 12,-20 C 24,5.37 18.63,0 12,0 z'

    return {
      path: pinPath,
      fillColor: color,
      fillOpacity: 1,
      strokeWeight: 0,
      scale: 1.8,
      anchor: new google.maps.Point(12, 32),
      labelOrigin: new google.maps.Point(12, 12),
    }
  }

  set loading (value) {
    if (value) {
      this.setAttribute('loading', '')
      this.searchForm.setAttribute('loading', 'true')
    } else {
      this.removeAttribute('loading')
      this.searchForm.setAttribute('loading', 'false')
    }
  }

  get loading () {
    return this.hasAttribute('loading')
  }

  setupFilters () {
    const clearAllButton = this.filterDropdown.querySelector('button')
    clearAllButton.addEventListener('click', () => {
      this.clearAllFilters()
    })

    this.updateFilterButtonWidth()

    this.filterButton.addEventListener('click', (e) => {
      e.stopPropagation()
      this.filterDropdown.classList.toggle('active')
    })

    this.filterCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        const facility = checkbox.dataset.facility

        if (checkbox.checked) {
          this.activeFiltersList.add(facility)
        } else {
          this.activeFiltersList.delete(facility)
        }

        this.updateFilterCount()
        this.updateFilterButtonWidth()
        this.filterCampsites()
      })
    })

    this.filterDropdown.addEventListener('click', (e) => {
      e.stopPropagation()
    })

    document.addEventListener('click', () => {
      this.filterDropdown.classList.remove('active')
    })

    // Clear filters button
    const clearFiltersButton = this.querySelector('.campsites__filters-dropdown .button-primary')
    if (clearFiltersButton) {
      clearFiltersButton.addEventListener('click', () => {
        this.filterCheckboxes.forEach(checkbox => {
          checkbox.checked = false
        })
        this.activeFiltersList.clear()
        this.updateFilterCount()
        this.updateFilterButtonWidth()
        this.filterCampsites()
        this.filterDropdown.classList.add('hidden')
      })
    }
  }

  updateFilterCount () {
    const filterCount = this.activeFiltersList.size
    const buttonText = this.filterButton.querySelector('.campsites__filters--button--text')

    if (filterCount > 0) {
      buttonText.textContent = `${this.translations.filters} (${filterCount})`
    } else {
      buttonText.textContent = this.translations.filters
    }
  }

  updateFilterButtonWidth () {
    if (this.activeFiltersList.size > 0) {
      this.filterButton.classList.remove('md:w-fit')
      this.filterButton.classList.add('md:w-[150px]')
    } else {
      this.filterButton.classList.remove('md:w-[150px]')
      this.filterButton.classList.add('md:w-fit')
    }
  }

  filterCampsites () {
    let visibleCount = 0
    const locationsList = this.querySelector('.campsites__locations-list')
    let noResultsElement = this.querySelector('.campsites__no-results')
    const noFilterMatchesMessage = this.querySelector('.campsites__no-filters-matches')

    if (noResultsElement) {
      noResultsElement.remove()
    }

    const currentZoom = this.map ? this.map.getZoom() : null
    const currentCenter = this.map ? this.map.getCenter() : null

    this.currentPage = 1
    let itemsShown = 0

    this.campsiteCards.forEach((card, index) => {
      let facilities
      try {
        facilities = JSON.parse(card.dataset.facilities)
        facilities = Array.isArray(facilities) ? facilities : [facilities]

        const shouldShow = this.activeFiltersList.size === 0 ||
          Array.from(this.activeFiltersList).every(filter => {
            const normalizedFilter = this.normalizeFacilityString(filter)
            return facilities.some(facility =>
              this.normalizeFacilityString(facility) === normalizedFilter,
            )
          })

        // Only hide the card if it doesn't match filters OR it's beyond pagination limit
        const withinPaginationLimit = itemsShown < this.itemsPerPage
        card.classList.toggle('hidden', !shouldShow || !withinPaginationLimit)

        if (shouldShow) {
          visibleCount++
          if (withinPaginationLimit) {
            itemsShown++
          }
        }

        // Update marker visibility based ONLY on filters, not pagination
        const marker = this.campsiteMarkers[index]
        if (marker) {
          marker.setVisible(shouldShow)
        }
      } catch (e) {
        console.error('Error parsing facilities:', e)
        facilities = []
      }
    })

    // Update the "no filter matches" message in the dropdown
    if (noFilterMatchesMessage) {
      if (this.activeFiltersList.size > 0 && visibleCount === 0) {
        noFilterMatchesMessage.classList.remove('hidden')
        noFilterMatchesMessage.classList.add('flex')
      } else {
        noFilterMatchesMessage.classList.add('hidden')
        noFilterMatchesMessage.classList.remove('flex')
      }
    }

    this.updatePagination(visibleCount)

    if (visibleCount === 0) {
      noResultsElement = document.createElement('div')
      noResultsElement.className = 'campsites__no-results text-center py-8'
      noResultsElement.innerHTML = `
        <p class="text-lg text-battleship">${this.getAttribute('data-no-results-text')}</p>
        <button type="button" class="button button-primary mt-4 clear-filters">${this.getAttribute('data-clear-filters-text')}</button>
      `

      noResultsElement.querySelector('.clear-filters').addEventListener('click', () => {
        this.clearAllFilters()
      })

      locationsList.appendChild(noResultsElement)
    }

    if (this.map && currentZoom !== null && currentCenter !== null) {
      this.map.setZoom(currentZoom)
      this.map.setCenter(currentCenter)
    }
  }

  clearAllFilters () {
    const currentZoom = this.map ? this.map.getZoom() : null
    const currentCenter = this.map ? this.map.getCenter() : null
    const noFilterMatchesMessage = this.querySelector('.campsites__no-filters-matches')

    // Hide search title
    const searchTitle = this.querySelector('.campsites__locations-search-title')
    if (searchTitle) {
      searchTitle.classList.add('hidden')
    }

    this.filterCheckboxes.forEach(checkbox => {
      checkbox.checked = false
    })

    this.activeFiltersList.clear()
    this.updateFilterCount()
    this.updateFilterButtonWidth()

    // Hide the no matches message
    if (noFilterMatchesMessage) {
      noFilterMatchesMessage.classList.add('hidden')
      noFilterMatchesMessage.classList.remove('flex')
    }

    // First, make all markers visible
    this.campsiteMarkers.forEach(marker => {
      if (marker) {
        marker.setVisible(true)
      }
    })

    // Then apply the pagination rules through filterCampsites
    this.filterCampsites()

    // Make sure map focus is preserved
    if (this.map && currentZoom !== null && currentCenter !== null) {
      this.map.setCenter(currentCenter)
      this.map.setZoom(currentZoom)
    }
  }

  normalizeFacilityString (str) {
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  }

  initializeWithSortedCampsites () {
    const allCampsites = window.campsites || []

    const sortedCampsites = allCampsites.sort((a, b) => {
      if (a.featured === b.featured) {
        return a.name.localeCompare(b.name)
      }
      return b.featured ? 1 : -1
    })

    const campsitesWithCoords = sortedCampsites.map(campsite => {
      const [lat, lng] = campsite.longitude_latitude.split(',').map(coord => parseFloat(coord.trim()))
      return {
        ...campsite,
        pinLat: lat,
        pinLong: lng,
      }
    }).filter(campsite => !isNaN(campsite.pinLat) && !isNaN(campsite.pinLong))

    this.locationsContainer.classList.remove('hidden')
    const closestDealersText = this.querySelector('.campsites__closet-dealers-text')
    if (closestDealersText) {
      closestDealersText.classList.add('opacity-0')
    }

    const ukBounds = {
      north: 58.7,
      south: 50.0,
      west: -5.8,
      east: 1.8,
    }

    const center = { lat: 54.5, lng: -2.0 }
    this.initializeMap(center, campsitesWithCoords)

    if (this.map) {
      const bounds = new google.maps.LatLngBounds(
        new google.maps.LatLng(ukBounds.south, ukBounds.west),
        new google.maps.LatLng(ukBounds.north, ukBounds.east),
      )
      this.map.fitBounds(bounds)
    }

    this.currentPage = 1
    this.showInitialResults(campsitesWithCoords)
  }

  calculateCenterPoint (campsites) {
    if (!campsites || campsites.length === 0) return null

    let totalLat = 0
    let totalLng = 0
    let validCount = 0

    campsites.forEach(campsite => {
      if (campsite.pinLat && campsite.pinLong) {
        totalLat += campsite.pinLat
        totalLng += campsite.pinLong
        validCount++
      }
    })

    if (validCount === 0) return null

    return {
      lat: totalLat / validCount,
      lng: totalLng / validCount,
    }
  }

  showInitialResults (campsites) {
    this.currentCampsites = campsites

    const locationItems = this.querySelectorAll('.campsites__locations-list--item')

    // Hide search title
    const searchTitle = this.querySelector('.campsites__locations-search-title')
    if (searchTitle) {
      searchTitle.classList.add('hidden')
    }

    this.currentPage = 1

    locationItems.forEach((item, index) => {
      const shouldShow = index < this.itemsPerPage
      item.classList.toggle('hidden', !shouldShow)

      const campsite = campsites[index]
      if (!campsite) {
        item.classList.add('hidden')
        return
      }

      if (shouldShow) {
        const campsiteUrl = `/pages/campsites/${this.handleUrlString(campsite.name)}`

        item.classList.remove('hidden')

        item.dataset.facilities = JSON.stringify(campsite.facilities)

        const imageContainer = item.querySelector('.campsites__locations-list--item-image')
        const heroImage = window.campsiteImages[campsite.name]
        if (heroImage) {
          imageContainer.innerHTML = `
            <div class="aspect-w-16 aspect-h-9 w-full h-full">
              <img src="${heroImage}" alt="${campsite.name}" class="object-cover w-full h-full rounded-t">
            </div>
            <div class="campsites__locations-list--item-title absolute bottom-1 left-1 w-full h-full flex items-end justify-start z-[2]">
              <div class="text-white font-body font-normal normal-case text-xl p-4">${campsite.name}</div>
            </div>
          `
        }

        const locationText = item.querySelector('.campsites__locations-list--item-content-location-text')
        const distanceText = item.querySelector('.campsites__locations-list--item-content-location-distance')
        const separator = item.querySelector('.campsites__locations-list--item-content-location-separator')

        locationText.textContent = campsite.location_name
        distanceText.textContent = ''
        separator.style.display = 'none'

        const facilitiesColumns = item.querySelectorAll('.campsites__locations-list--item-content-facilities-column')
        const limitedFacilities = campsite.facilities.slice(0, 6)
        const facilitiesPerColumn = Math.ceil(limitedFacilities.length / 2)

        facilitiesColumns[0].innerHTML = limitedFacilities
          .slice(0, facilitiesPerColumn)
          .map(facility => {
            const facilityHandle = facility
              .toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-+|-+$/g, '')
            return `
              <div class="campsite__facilities-item flex items-center py-2">
                <span class="campsite__facilities-item-icon inline-flex items-center justify-center h-4 w-4 mr-2 shrink-0">
                  <svg class="w-full h-full">
                    <use href="#facility-${facilityHandle}"></use>
                  </svg>
                </span>
                <span class="campsite__facilities-item-text text-xs lg:text-md">
                  ${facility}
                </span>
              </div>
            `
          })
          .join('')

        facilitiesColumns[1].innerHTML = limitedFacilities
          .slice(facilitiesPerColumn)
          .map(facility => {
            const facilityHandle = facility
              .toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-+|-+$/g, '')
            return `
              <div class="campsite__facilities-item flex items-center py-2">
                <span class="campsite__facilities-item-icon inline-flex items-center justify-center h-4 w-4 mr-2 shrink-0">
                  <svg class="w-full h-full">
                    <use href="#facility-${facilityHandle}"></use>
                  </svg>
                </span>
                <span class="campsite__facilities-item-text text-xs lg:text-md">
                  ${facility}
                </span>
              </div>
            `
          })
          .join('')

        const shortDescription = item.querySelector('.campsites__locations-list--item-content-short-description')
        shortDescription.textContent = campsite.short_description

        const button = item.querySelector('a.button')
        const cardAnchor = item.querySelector('a[aria-label]')

        button.href = campsiteUrl
        button.textContent = this.getAttribute('data-view-campsite-text')
        cardAnchor.href = campsiteUrl
      }
    })

    this.updatePagination(locationItems.length)
  }

  updatePagination (totalItems) {
    if (!this.paginationContainer) return

    const totalPages = Math.ceil(totalItems / this.itemsPerPage)
    console.log(`Total items: ${totalItems}, Items per page: ${this.itemsPerPage}, Total pages: ${totalPages}, Current page: ${this.currentPage}`)

    if (totalItems > (this.currentPage * this.itemsPerPage)) {
      this.paginationContainer.classList.remove('hidden')
      this.paginationContainer.classList.add('flex')

      this.paginationContainer.innerHTML = `
        <button type="button" class="button button-sheep button-xs">
          ${this.getAttribute('data-show-more-text')}
        </button>
      `

      const showMoreButton = this.paginationContainer.querySelector('button')
      showMoreButton.addEventListener('click', () => this.loadMoreItems())
    } else {
      this.paginationContainer.classList.add('hidden')
      this.paginationContainer.classList.remove('flex')
    }
  }

  loadMoreItems () {
    const locationItems = this.querySelectorAll('.campsites__locations-list--item')
    const startIndex = this.currentPage * this.itemsPerPage
    const endIndex = startIndex + this.itemsPerPage

    locationItems.forEach((item, index) => {
      if (index >= startIndex && index < endIndex) {
        const campsite = this.currentCampsites[index]
        if (!campsite) {
          item.classList.add('hidden')
          return
        }

        item.classList.remove('hidden')

        const campsiteUrl = `/pages/campsites/${this.handleUrlString(campsite.name)}`

        const imageContainer = item.querySelector('.campsites__locations-list--item-image')
        const heroImage = window.campsiteImages[campsite.name]
        if (heroImage) {
          imageContainer.innerHTML = `
            <div class="aspect-w-16 aspect-h-9 w-full h-full">
              <img src="${heroImage}" alt="${campsite.name}" class="object-cover w-full h-full rounded-t">
            </div>
            <div class="campsites__locations-list--item-title absolute bottom-1 left-1 w-full h-full flex items-end justify-start z-[2]">
              <div class="text-white font-body font-normal normal-case text-xl p-4">${campsite.name}</div>
            </div>
          `
        }

        const locationText = item.querySelector('.campsites__locations-list--item-content-location-text')
        const distanceText = item.querySelector('.campsites__locations-list--item-content-location-distance')
        const separator = item.querySelector('.campsites__locations-list--item-content-location-separator')

        locationText.textContent = campsite.location_name

        if (campsite.distance !== null && campsite.distance !== undefined) {
          distanceText.textContent = `${campsite.distance}km`
          separator.style.display = 'inline'
        } else {
          distanceText.textContent = ''
          separator.style.display = 'none'
        }

        const facilitiesColumns = item.querySelectorAll('.campsites__locations-list--item-content-facilities-column')
        const limitedFacilities = campsite.facilities.slice(0, 6)
        const facilitiesPerColumn = Math.ceil(limitedFacilities.length / 2)

        facilitiesColumns[0].innerHTML = limitedFacilities
          .slice(0, facilitiesPerColumn)
          .map(facility => {
            const facilityHandle = this.normalizeFacilityString(facility)
            return `
              <div class="campsite__facilities-item flex items-center py-2">
                <span class="campsite__facilities-item-icon inline-flex items-center justify-center h-4 w-4 mr-2 shrink-0">
                  <svg class="w-full h-full">
                    <use href="#facility-${facilityHandle}"></use>
                  </svg>
                </span>
                <span class="campsite__facilities-item-text text-xs lg:text-md">
                  ${facility}
                </span>
              </div>
            `
          })
          .join('')

        facilitiesColumns[1].innerHTML = limitedFacilities
          .slice(facilitiesPerColumn)
          .map(facility => {
            const facilityHandle = this.normalizeFacilityString(facility)
            return `
              <div class="campsite__facilities-item flex items-center py-2">
                <span class="campsite__facilities-item-icon inline-flex items-center justify-center h-4 w-4 mr-2 shrink-0">
                  <svg class="w-full h-full">
                    <use href="#facility-${facilityHandle}"></use>
                  </svg>
                </span>
                <span class="campsite__facilities-item-text text-xs lg:text-md">
                  ${facility}
                </span>
              </div>
            `
          })
          .join('')

        const shortDescription = item.querySelector('.campsites__locations-list--item-content-short-description')
        shortDescription.textContent = campsite.short_description

        const button = item.querySelector('a.button')
        const cardAnchor = item.querySelector('a[aria-label]')

        button.href = campsiteUrl
        button.textContent = this.getAttribute('data-view-campsite-text')
        cardAnchor.href = campsiteUrl
      }
    })

    this.currentPage++

    this.updatePagination(locationItems.length)
  }
}

if (!customElements.get('campsites-search')) {
  customElements.define('campsites-search', CampsitesSearch)
}
